import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Mail } from "lucide-react";
import Logo from "@/components/Logo";
import { EMAIL } from "@/data/content";

const columns = [
  {
    title: "About",
    links: [
      { to: "/about", label: "About IBMCT" },
      { to: "/about#vision", label: "Our Vision" },
      { to: "/about/journey", label: "Our Journey" },
      { to: "/leadership", label: "Spiritual Guidance" },
    ],
  },
  {
    title: "Our Work",
    links: [
      { to: "/our-work/namma-mane", label: "Namma Mane" },
      { to: "/our-work/tavaru", label: "Tavaru" },
      { to: "/our-work/thottilu", label: "Thottilu" },
      { to: "/our-work/dhamma-meditation", label: "Dhamma & Meditation" },
      { to: "/maha-karuna", label: "Maha Karuna 2026" },
      { to: "/dhamma", label: "Dhamma & Heritage" },
    ],
  },
  {
    title: "Connect",
    links: [
      { to: "/contact", label: "Contact Us" },
      { to: "/media/photos", label: "Media & Gallery" },
      { to: "/volunteer", label: "Volunteer" },
      { to: "/nalanda", label: "Nalanda Study Centre" },
    ],
  },
  {
    title: "Support",
    links: [
      { to: "/donate", label: "Donate Online" },
      { to: "/donate/bank-details", label: "Bank Transfer" },
      { to: "/donate/receipt", label: "80G Receipt" },
      { to: "/nalanda#support", label: "Support Nalanda" },
    ],
  },
];

const socials = [
  { icon: Facebook, label: "Facebook", href: "https://www.facebook.com" },
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com" },
  { icon: Youtube, label: "YouTube", href: "https://www.youtube.com" },
  { icon: Mail, label: "Email", href: `mailto:${EMAIL}` },
];

const Footer = () => (
  <footer className="relative overflow-hidden bg-ink text-white" data-testid="site-footer">
    <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-4 mb-5">
            <Logo height={64} testId="footer-logo-video" />
            <div>
              <p className="font-serif text-[26px] font-semibold text-white leading-tight">IBMCT</p>
              <p className="text-xs uppercase tracking-[0.16em] text-[rgba(255,255,255,0.5)]">Atta Deepo Bhava</p>
            </div>
          </div>
          <p className="text-[15px] leading-relaxed text-[rgba(255,255,255,0.68)] max-w-sm">
            International Buddhist Monks Charitable Trust — a registered charitable trust committed to social welfare, education,
            healthcare, cultural preservation and community development.
          </p>
          <p className="text-[13px] mt-4 text-[rgba(255,255,255,0.5)] leading-relaxed">
            Reg: BK 4 IV 59/12-13
            <br />
            LEI: 984500F817DEBC7D0R18
          </p>
          <div className="mt-6 flex items-center gap-3" data-testid="footer-social-links">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target={s.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                aria-label={s.label}
                className="w-11 h-11 rounded-full border border-[rgba(255,255,255,0.18)] flex items-center justify-center text-[rgba(255,255,255,0.7)] hover:text-charcoal hover:bg-saffron hover:border-saffron transition-colors duration-200"
                data-testid={`footer-social-${s.label.toLowerCase()}`}
              >
                <s.icon size={18} strokeWidth={1.75} aria-hidden="true" />
              </a>
            ))}
          </div>
          <Link to="/donate" className="btn btn-primary !rounded-full mt-7" data-testid="footer-donate-button">
            Donate
          </Link>
        </div>

        {columns.map((col) => (
          <nav key={col.title} aria-label={`Footer — ${col.title}`} data-testid={`footer-column-${col.title.toLowerCase().replace(/\s/g, "-")}`}>
            <h3 className="font-sans text-[13px] font-extrabold uppercase tracking-[0.22em] text-saffron-light mb-4">{col.title}</h3>
            <ul className="space-y-1">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="footer-link" data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>

      <div className="mt-14 pt-8 border-t border-[rgba(255,255,255,0.1)] text-center">
        <p className="font-serif italic text-[17px] text-[rgba(255,255,255,0.72)]" data-testid="footer-blessing">
          NAMO TASSA BHAGAVATO ARAHATO SAMMASAMBUDDHASSA — Bhavatu Sabba Mangalam. May All Beings Be Happy.
        </p>
        <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-[13px] text-[rgba(255,255,255,0.5)]">
          <span>© 2026 IBMCT. All Rights Reserved.</span>
          <Link to="/contact" className="footer-link" data-testid="footer-link-privacy-policy">Privacy Policy</Link>
          <Link to="/contact" className="footer-link" data-testid="footer-link-terms-conditions">Terms &amp; Conditions</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
